import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { DetailsRow, SelectionMode } from 'office-ui-fabric-react/lib/DetailsList';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import BundleVersionListRowDetails from '../BundleVersionListRowDetails';
import { useBundleVersionContext } from '../../context/bundle-version';

import styles from './BundleVersionListRow.style';
import BundleVersionListRevisionRowDetails from '../BundleVersionListRevisionRowDetails/BundleVersionListRevisionRowDetails';

const BundleVersionListRow = React.forwardRef(
  (
    { bundleVersion, onUpdate, onSuccess, className: classNameProp, edit, isEditMode, isLatest },
    ref,
  ) => {
    const { toggleIsOpen, isOpen } = useBundleVersionContext();
    const rowClassName = clsx(styles.row, classNameProp);
    const className = clsx(styles.root, {});
    const [bundleVersionRow, setBundleVersionRow] = useState(bundleVersion);

    const baseColumns = [
      ...bundleVersionRow.columns.slice(0, bundleVersionRow.columns.length - 1),
      {
        key: 'detailsIcon',
        name: '',
        headerClassName: styles.iconColumnHeader,
        iconClassName: styles.headerIcon,
        ariaLabel: 'icon',
        isIconOnly: true,
        fieldName: 'detailsIcon',
        minWidth: 14,
        maxWidth: 14,
        onRender: () => {
          return (
            <div className={clsx(styles.iconColumn)}>
              <FontIcon
                iconName={isOpen ? 'ChevronUpMed' : 'ChevronDownMed'}
                ariaLabel="TextDocument"
              />
            </div>
          );
        },
      },
    ];

    const handleClick = () => {
      setBundleVersionRow({
        ...bundleVersionRow,
        isOpen: !isOpen,
      });
      toggleIsOpen();
    };

    useEffect(() => {
      if (
        (isEditMode && isOpen) ||
        (!isEditMode && ((!isLatest && isOpen) || (isLatest && !isOpen)))
      ) {
        toggleIsOpen();
      }
    }, [isEditMode, isLatest]);

    useEffect(() => {
      if (bundleVersion) {
        setBundleVersionRow(bundleVersion);
      }
    }, [bundleVersion]);

    return (
      <>
        <div id={`bundleVersionRow${bundleVersion?.item?.version}`} role="rowgroup">
          <DetailsRow
            {...bundleVersionRow}
            selectionMode={SelectionMode.none}
            data-testid="detail-row"
            aria-roledescription="row"
            className={rowClassName}
            columns={baseColumns}
            onClick={handleClick}
          />
        </div>
        {edit ? (
          <BundleVersionListRevisionRowDetails
            isOpen={isOpen}
            className={className}
            bundleVersion={bundleVersion.item}
            ref={ref}
            onUpdate={onUpdate}
            onSuccess={onSuccess}
          />
        ) : (
          <BundleVersionListRowDetails
            isOpen={isOpen}
            className={className}
            bundleVersion={bundleVersion.item}
          />
        )}
      </>
    );
  },
);

BundleVersionListRow.propTypes = {
  bundleVersion: PropTypes.shape({
    item: PropTypes.shape({
      version: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      componentAllocationPercentage: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),
  className: PropTypes.string,
  edit: PropTypes.bool,
  isEditMode: PropTypes.bool,
  isLatest: PropTypes.bool,
  onUpdate: PropTypes.func,
  onSuccess: PropTypes.func,
};

BundleVersionListRow.defaultProps = {
  bundleVersion: {
    item: {
      startDate: '',
      endDate: '',
      componentAllocationPercentage: [],
    },
  },
  className: '',
  edit: false,
  isEditMode: false,
  isLatest: false,
  onUpdate: () => {},
  onSuccess: () => {},
};

export default BundleVersionListRow;
